import { createBaseApi } from '../baseApi';
import { setCredentials } from '../../slices/authSlice';

const authApi = createBaseApi('authApi');

export const enhancedAuthApi = authApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentUser: builder.query({
      query: () => ({
        url: `/users/me`,
        method: 'GET'
      }),
      providesTags: ['User'],
      refetchOnMountOrArgChange: false,
      keepUnusedDataFor: 86400,
      transformResponse: (response) => {
        if (response) {
          // Normalize profile picture (take first if array, or object, or null)
          let profilePic = null;
          if (response.profilePic) {
            if (Array.isArray(response.profilePic) && response.profilePic.length > 0) {
              profilePic = response.profilePic[0];
            } else if (response.profilePic.url) {
              profilePic = response.profilePic;
            }
          } else if (response.profilePicUrl || response.ProfilePicUrl) {
            // Use external profile picture URL as fallback
            profilePic = { url: response.profilePicUrl || response.ProfilePicUrl };
          }

          return {
            ...response,
            profilePic
          };
        }
        return response;
      }
    }),
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials
      }),
      invalidatesTags: ['Auth', 'User']
    }),
    logout: builder.mutation({
      query: () => ({
        url: '/auth/logout',
        method: 'POST'
      }),
      async queryFn(_, { dispatch }, _extraOptions, baseQuery) {
        return { data: { success: true } };
      },
      invalidatesTags: ['Auth', 'User']
    }),
    register: builder.mutation({
      query: (userData) => ({
        url: '/auth/local/register',
        method: 'POST',
        body: userData
      }),
      invalidatesTags: ['Auth', 'User']
    }),
    nativeLogin: builder.mutation({
      query: (credentials) => ({
        url: '/auth/native-login',
        method: 'POST',
        body: credentials
      }),
      invalidatesTags: ['Auth', 'User']
    }),
    // Password management endpoints
    customRequestPasswordReset: builder.mutation({
      query: ({ email, platform }) => ({
        url: '/auth/custom-forgot-password',
        method: 'POST',
        body: { email, platform }
      })
    }),
    customResetPassword: builder.mutation({
      query: ({ token, password, passwordConfirmation }) => ({
        url: '/auth/custom-reset-password',
        method: 'POST',
        body: { token, password, passwordConfirmation }
      }),
      invalidatesTags: ['Auth', 'User']
    }),
    // Email verification endpoints
    customSendVerificationEmail: builder.mutation({
      query: (email) => ({
        url: '/auth/custom-send-email',
        method: 'POST',
        body: { email }
      })
    }),
    customVerifyEmail: builder.mutation({
      query: (token) => ({
        url: '/auth/custom-verify-email',
        method: 'POST',
        body: { token }
      }),
      invalidatesTags: ['Auth', 'User']
    }),
    // Phone verification endpoints
    sendPhoneVerification: builder.mutation({
      query: (phoneNumber) => ({
        url: '/auth/phone/send-verification',
        method: 'POST',
        body: { phoneNumber }
      })
    }),
    verifyPhoneNumber: builder.mutation({
      query: (verificationCode) => ({
        url: '/auth/phone/verify',
        method: 'POST',
        body: { verificationCode }
      }),

      invalidatesTags: ['Auth', 'User']
    }),
    getPhoneVerificationStatus: builder.query({
      query: () => ({
        url: '/auth/phone/status',
        method: 'GET'
      }),
      providesTags: ['Auth', 'User']
    }),
    // Social login endpoints
    getGoogleAuthUrl: builder.query({
      query: (params) => {
        // Handle both old string format and new object format
        if (typeof params === 'string') {
          return {
            url: `/auth/social-auth-url/google?return_to=${encodeURIComponent(params)}`,
            method: 'GET'
          };
        }

        // New object format with platform support
        const { return_to = '/dashboard', platform = 'sheyou' } = params || {};
        const queryParams = new URLSearchParams({
          return_to: return_to,
          platform: platform
        });

        return {
          url: `/auth/social-auth-url/google?${queryParams.toString()}`,
          method: 'GET'
        };
      }
    }),
    getAppleAuthUrl: builder.query({
      query: (params) => {
        const { return_to = '/dashboard', platform = 'sheyou' } = params || {};
        const queryParams = new URLSearchParams({
          return_to: return_to,
          platform: platform
        });

        return {
          url: `/auth/social-auth-url/apple?${queryParams.toString()}`,
          method: 'GET'
        };
      }
    }),
    // Get user info after social login with token
    getCurrentUserAfterSocialLogin: builder.mutation({
      query: (token) => ({
        url: '/users/me',
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      }),
      async onQueryStarted(token, { dispatch, queryFulfilled }) {
        try {
          const { data: user } = await queryFulfilled;
          // After getting user info, set complete credentials
          dispatch(setCredentials({ user, token }));
        } catch (error) {
          console.error('Failed to get user info after social login:', error);
        }
      },
      transformResponse: (response) => {
        if (response) {
          // Normalize profile picture
          let profilePic = null;
          if (response.profilePic) {
            if (Array.isArray(response.profilePic) && response.profilePic.length > 0) {
              profilePic = response.profilePic[0];
            } else if (response.profilePic.url) {
              profilePic = response.profilePic;
            }
          } else if (response.profilePicUrl || response.ProfilePicUrl) {
            profilePic = { url: response.profilePicUrl || response.ProfilePicUrl };
          }

          return {
            ...response,
            profilePic
          };
        }
        return response;
      },
      invalidatesTags: ['Auth', 'User']
    }),
    getClaimInfo: builder.query({
      query: (token) => ({
        url: `/auth/claim-info/${token}`,
        method: 'GET'
      })
    }),
    claimAccount: builder.mutation({
      query: ({ token, email }) => ({
        url: `/auth/claim/${token}`,
        method: 'POST',
        body: { email }
      })
    }),
  }),
  overrideExisting: false,
});

// Export hooks for usage in functional components
export const {
  useGetCurrentUserQuery,
  useLoginMutation,
  useLogoutMutation,
  useRegisterMutation,
  useCustomRequestPasswordResetMutation,
  useCustomResetPasswordMutation,
  useCustomSendVerificationEmailMutation,
  useCustomVerifyEmailMutation,
  useSendPhoneVerificationMutation,
  useVerifyPhoneNumberMutation,
  useGetPhoneVerificationStatusQuery,
  useGetGoogleAuthUrlQuery,
  useGetAppleAuthUrlQuery,
  useGetCurrentUserAfterSocialLoginMutation,
  useNativeLoginMutation,
  useGetClaimInfoQuery,
  useClaimAccountMutation,
} = enhancedAuthApi;

export default enhancedAuthApi;

